/**
 * User role constants
 * These roles determine user permissions in the application
 */

/**
 * Super Admin role with ultimate permissions
 * - Has all admin permissions plus access to sensitive operations
 * - Can access email sending functionality (/mnb/mail)
 * - Full access to all admin panel features
 */
export const ROLE_SUPER_ADMIN = 1133;

/**
 * Admin role with full access to the admin panel (except email)
 * - Can view all users, conversations, set prices, and trial periods
 * - Can add new users manually or select existing users from dropdown
 * - Sees the price setting form by default, can toggle to view user list
 * - Cannot access email sending functionality
 */
export const ROLE_ADMIN = 1134;

/**
 * Lexera role with restricted access to the admin panel
 * - Can only view and manage users with specific promo codes (lexera/n4zlex)
 * - Can set prices and trial durations for existing Lexera users via dropdown only
 * - Cannot view the full user list or add new users
 * - Cannot toggle between views - always sees the price setting form
 * - Cannot access email sending functionality
 */
export const ROLE_LEXERA = 1135;
// export const ROLE_MANAGER = 1135;

/**
 * Marketing admin role: everything the Admin role can do. Introduced for
 * team members who run content marketing but are not super admins.
 * The marketing panel itself moved to the dash repo on 2026-08-11.
 */
export const ROLE_MARKETING_ADMIN = 1136;
/**

 * Role checking helper functions
 */

/**
 * Promo codes associated with Lexera-owned users.
 * Includes legacy lowercase variant for backward compatibility.
 */
export const LEXERA_PROMO_CODES = ['Lexera', 'lexera', 'n4zlex'] as const;

/**
 * Check if a promo code belongs to the Lexera pool.
 */
export const isLexeraPromoCode = (promoCode: string | null | undefined): boolean => {
  const normalized = promoCode?.trim().toLowerCase();
  return normalized === 'lexera' || normalized === 'n4zlex';
};

/**
 * Check if user has super admin privileges (can access email functionality)
 */
export const isSuperAdmin = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN;
};

/**
 * Check if user has admin privileges (full admin access except email)
 */
export const isAdmin = (userRole: number | null): boolean => {
  return userRole === ROLE_ADMIN;
};

/**
 * Check if user has Lexera privileges (restricted admin access)
 */
export const isLexera = (userRole: number | null): boolean => {
  return userRole === ROLE_LEXERA;
};

/**
 * Check if user has full admin privileges (super admin or admin)
 */
export const hasFullAdminAccess = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

/**
 * Check if user has any admin panel access (super admin, admin, marketing admin, or lexera)
 */
export const hasAdminAccess = (userRole: number | null): boolean => {
  return (
    userRole === ROLE_SUPER_ADMIN ||
    userRole === ROLE_ADMIN ||
    userRole === ROLE_MARKETING_ADMIN ||
    userRole === ROLE_LEXERA
  );
};

/**
 * Check if user can create new users (super admin, admin, or marketing admin - not lexera)
 */
export const canCreateUsers = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

/**
 * Access to the engine benchmark lab (/mnb/benchmarki).
 *
 * Narrower than hasFullAdminAccess on purpose: a benchmark run calls real
 * models, so every click costs money, and the panel exposes the full model and
 * prompt layout of the research engine.
 *
 * Resolves to the product owner (ROLE_SUPER_ADMIN) plus ROLE_MARKETING_ADMIN,
 * whose only holder in dev and prod is kielski.michal@gmail.com (checked in
 * both Firebase projects on 2026-08-12). ROLE_ADMIN is deliberately excluded.
 *
 * To open the panel for every admin, add ROLE_ADMIN here AND to BENCH_ROLES in
 * backend/src/mnb_bench/authz.py - both sides must agree.
 */
export const hasEngineLabAccess = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

/**
 * Access to the source-tracking panel (/mnb/zrodla).
 *
 * Same pair as the engine lab: the product owner (ROLE_SUPER_ADMIN) plus
 * ROLE_MARKETING_ADMIN, whose only holder in dev and prod is
 * kielski.michal@gmail.com. ROLE_ADMIN is deliberately excluded - the panel
 * exposes the full ingestion layout (which sources, which pipelines, which
 * cron writes what) and links straight into the upstream registries.
 *
 * To open it for every admin, add ROLE_ADMIN here AND to SOURCES_ROLES in
 * backend/src/mnb_sources/authz.py - both sides must agree.
 */
export const hasSourcesPanelAccess = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

/**
 * Access to the internal documentation panel (/mnb/dokumentacja).
 *
 * Same narrow pair as the engine lab and source tracking: the product owner
 * (ROLE_SUPER_ADMIN) plus ROLE_MARKETING_ADMIN. The articles describe how the
 * software works internally - env variables, scripts, triggers, account
 * tooling - so this is engineering surface, not general admin surface.
 *
 * Purely client-side: the articles ship inside the panel bundle, there is no
 * backend endpoint to keep in sync. Widening it is a one-line change here.
 */
export const hasDocsPanelAccess = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

/**
 * Access to the two internal switches in Settings: admin mode (no daily query
 * limits + plan simulation) and debug mode (model badges, diagnostic panel).
 *
 * Same narrow pair as the engine lab, source tracking and the docs panel: the
 * product owner (ROLE_SUPER_ADMIN) plus ROLE_MARKETING_ADMIN, whose only holder
 * in dev and prod is kielski.michal@gmail.com (re-read from `Roles` in both
 * projects on 2026-08-25). Deliberately NOT ROLE_ADMIN: six accounts hold 1134
 * on production, and admin mode removes cost control on every one of them.
 *
 * Both switches are re-checked server-side, so widening this alone grants
 * nothing - admin mode also needs ADMIN_MODE_ROLES in
 * backend/src/firebase/admin_mode.py, and the debug panel's data needs
 * backend/src/debug/authz.py. All three must agree.
 */
export const hasInternalModesAccess = (userRole: number | null): boolean => {
  return userRole === ROLE_SUPER_ADMIN || userRole === ROLE_MARKETING_ADMIN;
};

